import loadable from '@loadable/component';
import { type FunctionComponent, useState } from 'react';
import { FEATURE_FLAGS } from '../../../constants/feature-flags';
import { OVERLAY_MARKER } from '../../../constants/general';
import { HOMEPAGE_LINK } from '../../../constants/links';
import { SearchBarProvider, useSearchBarReducerContext } from '../../../contexts/search-bar/search-bar.context';
import { useNotSmallBreakpointListener } from '../../../hooks/breakpoints/breakpoint-listeners.hook';
import { useFeature } from '../../../hooks/features/features.hooks';
import { useRouteChange } from '../../../hooks/routing/route-change.hook';
import { IconWithLabelButton } from '../../buttons/icon-with-label-button/icon-with-label-button.component';
import { Link } from '../../common-components/link/link.component';
import { ProjectHeaderDropdown } from '../../project-components/project-header-dropdown/project-header-dropdown.component';
import OmniHomeSvg from '../../svgs/omni-home.svg';
import MenuIcon from '../../svgs/untitled-ui/menu.svg';
import { CallCenterLink } from '../call-center-link/call-center-link.component';
import { CartLink } from '../cart-link/cart-link.component';
import { MobileAccountLink } from '../mobile-account-link/mobile-account-link.component';
import { SearchBar } from '../search-bar/search-bar.component';
import { logoDimensions, navMenuLayout, divider } from './mobile-nav.css';
import { MobileSearchBar } from './mobile-search-bar/mobile-search-bar.component';

const LoadableTypeaheadDrawer = loadable(
	() => import(/* webpackChunkName: "typeahead-drawer" */ '../typeahead-drawer/typeahead-drawer.component'),
	{ resolveComponent: ({ TypeaheadDrawer }) => TypeaheadDrawer }
);

const LoadableNavigationUI = loadable(() => import(/* webpackChunkName: "mobile-nav-ui" */ './nav-ui.component'), {
	resolveComponent: ({ NavigationUI }) => NavigationUI
});

export type MobileNavProps = {
	startOpen?: boolean; // can be used to trigger the drawer immediately
	startTabName?: string; // which tab should be selected
};

export const MobileNav: FunctionComponent<MobileNavProps> = (props) => (
	<SearchBarProvider>
		<MobileNavComponent {...props} />
	</SearchBarProvider>
);

/**
 * MobileNav - renders markup for the small navigation
 */
export const MobileNavComponent: FunctionComponent<MobileNavProps> = ({ startTabName, startOpen }) => {
	const [isNavigationOpen, setIsNavigationOpen] = useState(startOpen || false);
	const closeNavigation = () => setIsNavigationOpen(false);
	const showNavigation = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
		event.preventDefault();
		event.currentTarget.blur();
		setIsNavigationOpen(true);
	};

	const typeaheadInDrawer = useFeature(FEATURE_FLAGS.TYPEAHEAD_MOBILE_VIEW, { isSiteWide: true });
	const [typeaheadState, typeaheadDispatch] = useSearchBarReducerContext();

	// Make sure menu is closed if the user navigates to another page or viewport is no longer small
	useRouteChange(closeNavigation);
	useNotSmallBreakpointListener((isNotSmall) => {
		if (isNotSmall) {
			closeNavigation();
			typeaheadDispatch({ type: 'typeaheadClosed' });
		}
	});

	return (
		<>
			{isNavigationOpen && <LoadableNavigationUI startTabName={startTabName} closeNavigation={closeNavigation} />}
			<div className="flex flex-column items-center dn-ns">
				{/* Logo */}
				<Link url={HOMEPAGE_LINK} className="flex justify-center dn-ns w-100 tc pv2" ariaLabel="Navigate to home page">
					<OmniHomeSvg className={`w-100 ${logoDimensions}`} />
				</Link>
				{/* Search Bar */}
				<div className="w-100 ph3">
					{typeaheadInDrawer ? (
						<>
							<MobileSearchBar placeholder="Search" />
							{typeaheadState.showResults && <LoadableTypeaheadDrawer />}
						</>
					) : (
						<SearchBar placeholder="Search" />
					)}
				</div>
				{/* Nav Pane */}
				<div className={`${navMenuLayout} items-center w-100 pt1`}>
					{/* Menu */}
					<IconWithLabelButton
						onClick={showNavigation}
						label="Menu"
						icon={<MenuIcon data-testid="MenuIcon" />}
						buttonClass="pv2 w-100"
						ariaLabel="Site Navigation"
						theme="primary"
					/>
					{/* Account */}
					<MobileAccountLink theme="primary" />
					{/* Projects */}
					<ProjectHeaderDropdown />
					{/* Call */}
					<CallCenterLink theme="primary" containerClass="flex-row-ns" />
					{/* Cart */}
					<CartLink isMobile={true} />
				</div>
				{/* Divider */}
				<div className={`${OVERLAY_MARKER} db dn-ns`} />
				<div className={`w-100 mb1 ${divider}`} />
			</div>
		</>
	);
};
